import React from 'react';
import getConfig from 'next/config';
import Pagination, {
  PaginationRenderItemParams,
} from '@mui/material/Pagination';
import { PaginationItem } from '@mui/material';
import { useRouter } from 'next/router';
import { PaginationDataSet, paginationDefaults } from '../../types/pagination';
import { useStyles } from './SearchPagination.styles';
import NoStyleLink from '../ButtonsLinks/NoStyleLink';
import { ELASTICSEARCH_MAX_WINDOW_SIZE } from '../../constants';
import { sendAnalyticsEvent } from '../Analytics/utilities';

const { publicRuntimeConfig } = getConfig();

interface SearchPaginationProps {
  totalResults: number;
  setPaginationData: (i: PaginationDataSet) => void;
  paginationData: PaginationDataSet;
}

const SearchPagination = ({
  totalResults,
  setPaginationData,
  paginationData,
}: SearchPaginationProps) => {
  const classes = useStyles();
  const router = useRouter();
  const pageCount = Math.ceil(totalResults / paginationData.pageSize);

  const handleChange = (event, value) => {
    sendAnalyticsEvent('Clicked Pagination Item', 'Pagination', 'Click', {
      PageNumber: value,
      PageSize: paginationData.pageSize,
      PageCount: pageCount,
      TotalResults: totalResults,
    });
    event.preventDefault();
    setPaginationData({
      page: value,
      pageSize: paginationData.pageSize,
    });
    window.scrollTo(0, 0);
    document
      .querySelector('#resultsContainer')
      .firstElementChild?.querySelector('a')
      ?.focus();
  };

  const getPaginationUrl = (item: PaginationRenderItemParams) => {
    if (item.page) {
      const [currentPath, currentQuery] = router.asPath.split('?');
      const currentParams = currentQuery ? currentQuery.split('&') : [];

      const newParams = currentParams.filter(
        (p) => !p.startsWith('page=') && !p.startsWith('pageSize=')
      );
      if (item.page !== paginationDefaults.page) {
        newParams.push(`page=${item.page}`);
      }
      if (paginationData.pageSize !== paginationDefaults.pageSize) {
        newParams.push(`pageSize=${paginationData.pageSize}`);
      }

      const newPath = currentPath.startsWith(publicRuntimeConfig.basePath)
        ? currentPath
        : publicRuntimeConfig.basePath + currentPath;
      const newQuery = newParams.length > 0 ? `?${newParams.join('&')}` : '';

      return newPath + newQuery;
    }
    return '';
  };

  const maxPageNumber = Math.floor(
    ELASTICSEARCH_MAX_WINDOW_SIZE / paginationData.pageSize
  );

  return (
    <div className={classes.root}>
      <Pagination
        shape="rounded"
        onChange={handleChange}
        count={Math.min(pageCount, maxPageNumber)}
        page={paginationData.page}
        renderItem={(item) => {
          const content =
            item.page < 1 || item.page > pageCount ? (
              <></>
            ) : (
              // Sets href because it is good for SEO, but pagination is handled on handleChange
              <NoStyleLink href={getPaginationUrl(item)} tabIndex={-1}>
                <PaginationItem {...item} tabIndex={0} />
              </NoStyleLink>
            );
          return ['start-ellipsis', 'end-ellipsis'].includes(item.type) ? (
            <PaginationItem {...item} />
          ) : (
            content
          );
        }}
        hideNextButton={maxPageNumber === paginationData.page}
      />
    </div>
  );
};

export default SearchPagination;
